import { TooltipComponentPositionCallback, util } from 'echarts';
import { ThemeParser } from '../../../core/theme';
import { FONT_SIZE_LIST, LINE_HEIGHT_LIST } from '../../mobile/static/font';
import { Option } from '../../../core/option';
import type { PresetMode } from '../..';
import styles from '../styles.module.less';

/**
 * 竖直方向固定在与图表相切，水平方向跟随鼠标水平位置
 * @param point 鼠标位置，如 [20, 40]
 * @param params @see https://echarts.apache.org/zh/option.html#series-line.label.formatter
 * @param dom tooltip 的 dom 对象。
 * @param rect 只有鼠标在图形上时有效，是一个用x, y, width, height四个属性表达的图形包围盒。
 * @param size 包括 dom 的尺寸和 echarts 容器的当前尺寸，例如：{contentSize: [width, height], viewSize: [width, height]}。
 * @returns 可以是一个表示 tooltip 位置的数组，数组值可以是绝对的像素值，也可以是相 百分比。
 *          也可以是一个对象，如：{left: 10, top: 30}，或者 {right: '20%', bottom: 40}。
 */
const defaultTooltipPoistion = (chart: Parameters<ThemeParser>[1]) => {
  return (point, params, dom: HTMLElement, rect, size) => {
    const gridRight = 16;
    const gridLeft = 16;
    const HALF = 0.5;
    const ecIns = chart.getECharts();
    // 将鼠标水平位置转换为x轴坐标的索引
    const pt = ecIns.convertFromPixel({ xAxisIndex: 0 }, point[0]);
    // 将x轴坐标索引转换为像素坐标
    const ptPixel = ecIns.convertToPixel({ xAxisIndex: 0 }, pt);
    // 以tooltip左上角定位整个tooltip
    // tooltip左上角坐标最小值为容器最左侧
    const min = 0;
    // tooltip左上角坐标最大值为图表容器最右侧左偏tooltip一个宽度
    const max = size.viewSize[0] - size.contentSize[0];
    // 实际tooltip坐标跟随axisPointer 与axisPointer居中对齐
    const validRange = Math.max(
      min + gridLeft,
      Math.min(max - gridRight, ptPixel - size.contentSize[0] * HALF)
    );

    // 默认情况下首屏tooltip不显示， 只有需要获取位置（调用position时）显示tooltip
    // 所以tooltip的after伪类也应当如此，所以让after伪类的visibility属性跟随主类， 当tooltip调用position时显示主类，同时after同步显示
    dom.classList.add([styles['ainvest-tooltip-show']]);

    // 三角伪元素的动态偏移
    dom.style.setProperty(
      '--offset',
      `translate(calc( ${ptPixel - size.contentSize[0] * HALF - validRange + HALF}px - 50% ), 0)`
    );

    return [validRange, -size.contentSize[1] - 9];
  };
};

function parseTooltip(
  option: Parameters<ThemeParser>[0],
  chart: Parameters<ThemeParser>[1],
  themeMode: PresetMode
) {
  const positionHandler = defaultTooltipPoistion(
    chart
  ) as unknown as TooltipComponentPositionCallback;
  const defaultTooltip: Option['tooltip'] = {
    show: true,
    position: positionHandler,
    className: `
    ${themeMode === 'light' ? styles['ainvest-tooltip-light'] : styles['ainvest-tooltip-dark']}  
    ${styles['ainvest-tooltip']}
    `,
    backgroundColor: themeMode === 'light' ? '#EBEBEB' : '#383838',
    borderColor: 'transparent',
    textStyle: {
      color: themeMode === 'light' ? '#000' : '#fff',
      fontSize: FONT_SIZE_LIST.tooltipLabel,
      lineHeight: LINE_HEIGHT_LIST.tooltipLabel,
      fontFamily: 'sans-serif'
    },
    transitionDuration: 0,
    extraCssText: 'box-shadow: 0 0 0 rgba(0, 0, 0, 0); border-radius: 4px; z-index: 1',
    /** echarts默认配置为'item'。
     * 在option.animation = true 的情况下，trigger: 'item'会导致xAxis.axisPointer在mousemove时不能紧跟鼠标。
     * 感觉应该是trigger: 'item'的交互是在鼠标停留在相对稳定的状态下触发axisPointer，有延迟; 'axis'则是鼠标每移动到一个x坐标位置，就触发交互。
     * 在不设置tooltip时，不会有xAxis.axisPointer交互跟不上鼠标的情况
     * */
    trigger: 'axis',
    axisPointer: {
      animation: false
    },
    /** 交互默认配置 */
    hideDelay: 0,
    dvAutoHide: true
  };
  option.tooltip = util.merge(util.clone(defaultTooltip), option.tooltip, true);
}

export function tooltipThemeParse(...args: Parameters<ThemeParser>) {
  const [option, chart] = args;
  const themeMode = chart.getModel().getThemeName().includes('light') ? 'light' : 'dark';
  parseTooltip(option, chart, themeMode);
}
